import {
  cloneFilter,
  emptyAdvancedDraft,
  emptyAppliedFilter,
  migrateAppliedFilter,
  quickAppliedFilter,
} from "@/services/layers/advancedFilters";
import {
  effectiveAttributeFilter,
  isAttributeFilterEnabled,
  readSavedAttributeFilter,
  writeSavedAttributeFilter,
} from "@/services/layers/attributeFilterState";

const legacyFiltersKey = layerId => `attrTable_filters_${layerId}`;
const appliedKey = layerId => `attrTable_applied_v1_${layerId}`;

const normalizeFilters = filters => Object.keys(filters || {}).reduce((result, key) => {
  const filter = filters[key];
  result[key] = filter && typeof filter === "object" && filter.operator
    ? filter
    : { operator: "contains", value: filter == null ? "" : String(filter) };
  return result;
}, {});

const readSessionJSON = (key, fallback) => {
  try {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    console.warn(`[attributeTable] No se pudo restaurar ${key}:`, error);
    return fallback;
  }
};

const restoredAppliedFilter = layerId => {
  const restored = readSessionJSON(appliedKey(layerId), null);
  if (restored && [1, 2].includes(restored.version) && ["none", "quick", "advanced"].includes(restored.mode)) return migrateAppliedFilter(restored);
  const legacyQuick = normalizeFilters(readSessionJSON(legacyFiltersKey(layerId), {}));
  return Object.keys(legacyQuick).length ? quickAppliedFilter(legacyQuick) : emptyAppliedFilter();
};

const initialState = () => ({
  visible: false,
  layer: null,
  attributes: [],
  totalFeatures: 0,
  columnFilters: {},
  fieldSchema: {},
  fieldAliases: {},
  metadata: null,
  appliedFilter: emptyAppliedFilter(),
  // totalUnfiltered: cantidad total de registros de la capa sin importar el
  // filtro (ver attributesTable.js::fetchTotalCount) — null mientras no se
  // ha resuelto todavía (llega después, no bloquea la apertura de la tabla).
  totalUnfiltered: null,
  advancedDraft: emptyAdvancedDraft(),
  advancedPanelOpen: false,
  advancedPanelMinimized: false,
  layoutHeight: 0,
  loading: false,
  // pendingSavedFiltersModal: seteado por ListPlegable.vue::clickManageSavedFilters
  // antes de abrir la tabla de una capa apagada — AttributesTable.vue lo
  // consume y resetea al montar para abrir el modal de filtros guardados de
  // una vez, sin depender de un evento entre componentes hermanos.
  pendingSavedFiltersModal: false,
});

export default {
  namespaced: true,
  state: initialState(),

  mutations: {
    SHOW_TABLE(state, payload) {
      state.visible = true;
      state.layer = payload.layer;
      state.attributes = payload.attributes || [];
      state.totalFeatures = payload.totalFeatures || 0;
      state.columnFilters = payload.columnFilters || {};
      state.fieldSchema = payload.fieldSchema || {};
      state.fieldAliases = payload.fieldAliases || {};
      state.metadata = payload.metadata || null;
      state.appliedFilter = payload.appliedFilter || emptyAppliedFilter();
      state.totalUnfiltered = null;
      state.advancedDraft = payload.advancedDraft || emptyAdvancedDraft();
      state.advancedPanelOpen = !!payload.advancedPanelOpen;
      state.advancedPanelMinimized = false;
      state.layoutHeight = 280;
      state.loading = true;
    },
    UPDATE_ATTRIBUTES(state, payload) {
      state.attributes = payload.attributes || [];
      state.totalFeatures = Number(payload.totalFeatures) || 0;
    },
    HIDE_TABLE(state) {
      Object.assign(state, initialState());
    },
    SET_COLUMN_FILTER(state, { key, filter }) {
      state.columnFilters = { ...state.columnFilters, [key]: filter };
    },
    CLEAR_COLUMN_FILTERS(state) {
      state.columnFilters = {};
    },
    SET_APPLIED_FILTER(state, filter) {
      state.appliedFilter = cloneFilter(filter || emptyAppliedFilter());
      if (state.appliedFilter.mode === "quick") state.columnFilters = cloneFilter(state.appliedFilter.quick || {});
      if (state.appliedFilter.mode === "advanced") state.advancedDraft = cloneFilter(state.appliedFilter.advanced);
    },
    SET_ADVANCED_DRAFT(state, draft) {
      state.advancedDraft = cloneFilter(draft || emptyAdvancedDraft());
    },
    SET_ADVANCED_PANEL(state, open) {
      state.advancedPanelOpen = !!open;
      if (!open) state.advancedPanelMinimized = false;
    },
    SET_ADVANCED_PANEL_MINIMIZED(state, minimized) {
      state.advancedPanelMinimized = !!minimized;
    },
    SET_LAYOUT_HEIGHT(state, height) {
      state.layoutHeight = height;
    },
    SET_FIELD_METADATA(state, payload) {
      state.fieldSchema = payload.schema || {};
      state.fieldAliases = payload.aliases || {};
      state.metadata = payload.metadata || null;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_PENDING_SAVED_FILTERS_MODAL(state, pending) {
      state.pendingSavedFiltersModal = !!pending;
    },
    SET_TOTAL_UNFILTERED(state, total) {
      state.totalUnfiltered = total == null ? null : Number(total) || 0;
    },
  },

  actions: {
    openTable({ commit }, payload) {
      const persistedFilter = readSavedAttributeFilter(payload.layer.id);
      const savedFilter = persistedFilter || restoredAppliedFilter(payload.layer.id);
      const appliedFilter = persistedFilter ? effectiveAttributeFilter(payload.layer.id) : savedFilter;
      commit("SHOW_TABLE", {
        ...payload,
        appliedFilter,
        columnFilters: savedFilter.mode === "quick" ? cloneFilter(savedFilter.quick || {}) : {},
        advancedDraft: savedFilter.mode === "advanced" ? cloneFilter(savedFilter.advanced) : emptyAdvancedDraft(),
      });
    },
    updateAttributes({ commit }, payload) { commit("UPDATE_ATTRIBUTES", payload); },
    closeTable({ commit }) { commit("HIDE_TABLE"); },
    setColumnFilter({ commit }, payload) { commit("SET_COLUMN_FILTER", payload); },
    clearColumnFilters({ commit }) { commit("CLEAR_COLUMN_FILTERS"); },
    setAdvancedDraft({ commit }, draft) { commit("SET_ADVANCED_DRAFT", draft); },
    setAdvancedPanel({ commit }, open) { commit("SET_ADVANCED_PANEL", open); },
    setAdvancedPanelMinimized({ commit }, minimized) { commit("SET_ADVANCED_PANEL_MINIMIZED", minimized); },
    commitAppliedFilter({ commit, state }, filter) {
      const applied = cloneFilter(filter || emptyAppliedFilter());
      if (state.layer) writeSavedAttributeFilter(state.layer.id, applied);
      commit("SET_APPLIED_FILTER", applied);
    },
    setEffectiveFilter({ commit }, filter) { commit("SET_APPLIED_FILTER", filter); },
    setLayoutHeight({ commit }, height) { commit("SET_LAYOUT_HEIGHT", height); },
    setFieldMetadata({ commit }, payload) { commit("SET_FIELD_METADATA", payload); },
    setFieldSchema({ commit }, schema) { commit("SET_FIELD_METADATA", { schema }); },
    setLoading({ commit }, loading) { commit("SET_LOADING", loading); },
    setPendingSavedFiltersModal({ commit }, pending) { commit("SET_PENDING_SAVED_FILTERS_MODAL", pending); },
    setTotalUnfiltered({ commit }, total) { commit("SET_TOTAL_UNFILTERED", total); },
  },

  getters: {
    isVisible: s => s.visible,
    layer: s => s.layer,
    attributes: s => s.attributes,
    totalFeatures: s => s.totalFeatures,
    columnFilters: s => s.columnFilters,
    fieldSchema: s => s.fieldSchema,
    fieldAliases: s => s.fieldAliases,
    metadata: s => s.metadata,
    appliedFilter: s => s.appliedFilter,
    advancedDraft: s => s.advancedDraft,
    advancedPanelOpen: s => s.advancedPanelOpen,
    advancedPanelMinimized: s => s.advancedPanelMinimized,
    layoutHeight: s => s.layoutHeight,
    loading: s => s.loading,
    pendingSavedFiltersModal: s => s.pendingSavedFiltersModal,
    totalUnfiltered: s => s.totalUnfiltered,
    // Compatibilidad temporal para consumidores anteriores.
    spatialFilter: s => {
      const roi = s.appliedFilter.mode === "advanced" && s.appliedFilter.advanced.roi;
      return roi && roi.enabled ? roi.spec : null;
    },
  },
};
